import Link from "next/link"
import Script from "next/script"
import Image from "components/Image"
import PageHeader from "components/DynamicComponent/atoms/PageHeader"
import CTABanner from "components/DynamicComponent/atoms/CTABanner"
import Meta from "components/Meta"
import CallToAction from "components/CallToAction"
import Testimonials from "components/DynamicComponent/molecules/Testimonials"
import { getAllTeamMemberPaths, getTeamMember, getGlobals } from "storyblok/api"
import { getStoryblokLink } from "utilities/getStoryblokLink"
import richText from "utilities/richText"
import StoryblokVisualEditor from "components/StoryblokVisualEditor"
import Placeholder from "public/assets/team-member-placeholder.svg"

export default function TeamMember({ story }) {
  const credentials = story?.content?.credentials ? `, ${story?.content?.credentials}` : ""

  const { twentysixty_seo } = story?.content

  const descriptionLocation = story?.content?.location?.length
    ? ` at our ${story?.content?.location[0]?.name ?? ""} office.`
    : "."

  // i.e. [Team Member Name], [Credentials e.g. MD] is a [Team Member Position] at our [primary location]...
  const description = `${story?.content?.name}${credentials} is part of our medical team${
    descriptionLocation ? descriptionLocation : "."
  } View their scheduling, contact, and background information.`

  return (
    <>
      <Meta
        info={{
          title: twentysixty_seo?.title ? twentysixty_seo?.title : story?.content?.name,
          og_title: twentysixty_seo?.title ? twentysixty_seo?.title : story?.content?.name,
          twitter_title: twentysixty_seo?.title ? twentysixty_seo?.title : story?.content?.name,
          og_image: story?.content?.photo?.filename ?? null,
          twitter_image: story?.content?.photo?.filename ?? null,
          description: twentysixty_seo?.description ? twentysixty_seo?.description : description,
          og_description: twentysixty_seo?.description ? twentysixty_seo?.description : description,
          twitter_description: twentysixty_seo?.description ? twentysixty_seo?.description : description,
        }}
      />
      <main>
        <PageHeader
          blok={{
            heading: "Our Medical Team",
          }}
        />
        <StoryblokVisualEditor story={story?.content}>
          <section className="px-8">
            <div className="team-member-grid lg:grid max-w-screen-xl mx-auto py-12">
              <div className="text-center team-member-bio pb-12 lg:pb-0">
                {story?.content?.photo?.filename ? (
                  <Image
                    src={story?.content?.photo?.filename}
                    alt=""
                    width={345}
                    height={345}
                    className="w-full aspect-square object-cover object-top"
                  />
                ) : (
                  <Placeholder className="w-full aspect-square object-cover object-top" />
                )}
                <h1 className="text-m1 py-2 font-primary uppercase">
                  {story?.content?.name}
                  {credentials}
                </h1>
                {story?.content?.title ? (
                  <>
                    <h2 className="font-primary uppercase text-base">Title</h2>
                    <p className="font-light font-secondary pb-4">{story?.content?.title}</p>
                  </>
                ) : null}
                {story?.content?.location &&
                story?.content?.location?.length > 0 &&
                story?.content?.location?.filter((location) => location?.name !== undefined)?.length > 0 ? (
                  <>
                    <h2 className="font-primary uppercase text-base">Location</h2>
                    <p className="font-light font-secondary pb-4">
                      {story?.content?.location?.map((location, idx) =>
                        location?.name ? (
                          <>
                            <CallToAction
                              key={idx}
                              href={`/locations/${location?.slug}`}
                              style="inline"
                            >{`${location?.name}${idx !== story?.content?.location.length - 1 ? ", " : ""}`}</CallToAction>{" "}
                          </>
                        ) : (
                          ""
                        ),
                      )}
                    </p>
                  </>
                ) : null}
                {story?.content?.additional_staff ? (
                  <div className="text-center prose-h3:font-primary prose-h3:uppercase prose-h3:text-base prose-p:font-light prose-p:font-secondary prose-p:pb-2 prose-a:mx-auto prose-a:my-0 prose-a:pb-3 pb-2">
                    {richText(story?.content?.additional_staff)}
                  </div>
                ) : null}
                <CallToAction href="/make-an-appointment">Schedule Appointment</CallToAction>
              </div>
              <div className="lg:pl-12 team-member-content content first:prose-headings:pt-0 first:prose-p:pt-0 prose-h2:pb-0 prose-h2:text-l2 prose-p:py-2 prose-headings:py-2 prose-ul:list-disc prose-ul:pl-4 prose-ol:list-decimal prose-ol:pl-4 marker:text-gray-dark marker:text-sm prose-p:font-light prose-p:leading-7">
                {richText(story?.content?.bio)}
              </div>
              {story?.content?.blogArticles?.length ? (
                <nav className="flex flex-col in-the-news gap-y-4 lg:self-start text-navy lg:border-l lg:border-l-blue lg:pl-4 mt-12">
                  <h2 className="font-secondary font-bold text-m1 leading-none uppercase w-max pb-2">
                    Dr. {story?.content?.name.split(" ")[1]} In The News
                  </h2>
                  {story?.content?.blogArticles?.map((article, idx) => (
                    <Link
                      href={`/blog/${article?.slug.toLowerCase()}`}
                      className="plain-navy-arrow font-primary text-left text-navy uppercase hover:underline relative"
                      key={idx}
                    >
                      {article?.title}
                    </Link>
                  ))}
                </nav>
              ) : null}
            </div>
          </section>
        </StoryblokVisualEditor>
        <Testimonials
          blok={{
            title: "Success stories",
            style: "light",
            testimonials: story?.content?.testimonials,
          }}
        />
      </main>
      <CTABanner />
      {story?.content?.code && (
        <Script type="application/ld+json" id="person-schema">
          {story?.content?.code}
        </Script>
      )}
      {story?.content?.videoschema && (
        <Script type="application/ld+json" id="video-schema">
          {story?.content?.videoschema}
        </Script>
      )}
    </>
  )
}

export async function getStaticProps({ params: { slug }, preview = null }) {
  const globals = await getGlobals()
  const story = await getTeamMember(slug, preview)

  return {
    props: {
      story: story ?? null,
      globals: globals ?? null,
      preview,
    },
  }
}

export async function getStaticPaths() {
  return {
    paths: await getAllTeamMemberPaths(),
    fallback: false,
  }
}
